import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { WritableDraft } from 'immer'
import type { RootState } from '..'
import { RsmPaginationPageSizes } from '../../rsmCoreComponents/components/RsmPagination/RsmPagination'

// These constants are KEYS and should match with
// InvoiceSelectedFiltersState for key here and server data for values returned by invoicefilters
// DO NOT change values here unless warranted.
export const InvoiceSelectedFiltersConstants = {
  Seperator: '##',
  DefaultCurrency: 'usd',
  searchString: 'search',
  invoiceStatus: 'status',
  invoiceDates: 'daterange',
  invoiceStartDate: 'invoiceStartDate',
  invoiceEndDate: 'invoiceEndDate',
  invoiceDate: 'date',
  invoiceAmounts: 'amountrange',
  invoiceAmountFrom: 'invoiceAmountFrom',
  invoiceAmountTo: 'invoiceAmountTo',
  currency: 'currency',
  invoiceIds: 'invoiceids',
  SortBy: 'date', // New constant for sorting
  isSortAscending: false, // New constant for sorting
  clientId: 'clientId',
}

export const setFilterValue = (
  category: string,
  filter: string | Date | null,
) => {
  // isNaN and Number.isNaN have different behavior, so need to disable the eslint rule.
  if (
    filter instanceof Date &&
    // eslint-disable-next-line no-restricted-globals
    !isNaN(filter as Date as unknown as number) &&
    filter !== null
  ) {
    const dt = filter as Date
    return `${category}${
      InvoiceSelectedFiltersConstants.Seperator
    }${dt.toISOString()}`
  }
  return `${category}${InvoiceSelectedFiltersConstants.Seperator}${filter}`
}

export const amountRangeFilterMax = 1000000

const setError = (
  state: WritableDraft<InvoiceSelectedFiltersState>,
  errorMessage: string,
) => {
  state.filterStateValidation = {
    isValid: false,
    errorMessages: [...state.filterStateValidation.errorMessages, errorMessage],
  }
}

const clearError = (state: WritableDraft<InvoiceSelectedFiltersState>) => {
  state.filterStateValidation = {
    isValid: true,
    errorMessages: [],
  }
}

const changeRowsPerPage = (
  state: WritableDraft<InvoiceSelectedFiltersState>,
  pageSize: RsmPaginationPageSizes,
) => {
  if (pageSize <= 0) setError(state, 'pagesize cannot be negative')
  else state.pageSize = pageSize
}

const changePageNumber = (
  state: WritableDraft<InvoiceSelectedFiltersState>,
  pageNumber: number,
) => {
  if (pageNumber <= 0) setError(state, 'pagenumber cannot be negative')
  else state.pageNumber = pageNumber
}

const changeSorting = (
  state: WritableDraft<InvoiceSelectedFiltersState>,
  sortBy: string,
  isAscending: boolean,
) => {
  state.sortby = sortBy
  state.isSortAscending = isAscending
}

const clearCategoryFilter = (
  state: WritableDraft<InvoiceSelectedFiltersState>,
  category: string,
) => {
  clearError(state)
  if (!category) {
    setError(state, 'category required for clear category')
    return
  }
  switch (category) {
    case InvoiceSelectedFiltersConstants.invoiceStatus:
      state.invoiceStatus = []
      break

    case InvoiceSelectedFiltersConstants.invoiceDates:
      state.invoiceStartDate = null
      state.invoiceEndDate = null
      state.invoiceDateRange = ''
      clearError(state)
      break

    case InvoiceSelectedFiltersConstants.invoiceAmountFrom:
      state.invoiceAmountFrom = null
      break

    case InvoiceSelectedFiltersConstants.invoiceAmountTo:
      state.invoiceAmountTo = null
      break

    case InvoiceSelectedFiltersConstants.invoiceAmounts:
      state.invoiceAmountFrom = null
      state.invoiceAmountTo = null
      clearError(state)
      break

    case InvoiceSelectedFiltersConstants.currency:
      state.currency = InvoiceSelectedFiltersConstants.DefaultCurrency
      break
    case InvoiceSelectedFiltersConstants.clientId:
      state.clientId = undefined
      clearError(state)
      break

    default:
      setError(
        state,
        'invalid category in filter, should be defined in constants locally',
      )
  }

  state.pageNumber = 1
}

const changeFilterValue = (
  state: WritableDraft<InvoiceSelectedFiltersState>,
  filter: string,
) => {
  clearError(state)
  const [category, value] = filter?.split(
    InvoiceSelectedFiltersConstants.Seperator,
  ) ?? [undefined, undefined]
  if (!category || !value) {
    setError(state, 'filter category and value not combined properly')
    return
  }
  let idx = -1
  switch (category) {
    case InvoiceSelectedFiltersConstants.invoiceStatus:
      idx = state.invoiceStatus.indexOf(value)
      if (idx >= 0) state.invoiceStatus.splice(idx, 1)
      else state.invoiceStatus.push(value)
      break

    case InvoiceSelectedFiltersConstants.invoiceStartDate:
      if (value === 'null') {
        state.invoiceStartDate = null
        state.invoiceDateRange = ''
      } else {
        state.invoiceStartDate = new Date(value)
      }
      break

    case InvoiceSelectedFiltersConstants.invoiceEndDate:
      if (value === 'null') {
        state.invoiceEndDate = null
        state.invoiceDateRange = ''
      } else {
        state.invoiceEndDate = new Date(value)
      }
      break

    case InvoiceSelectedFiltersConstants.invoiceDates:
      state.invoiceDateRange = value
      break

    case InvoiceSelectedFiltersConstants.invoiceAmountFrom:
      if (value === 'null') {
        state.invoiceAmountFrom = null
      } else {
        state.invoiceAmountFrom = Number(value)
      }
      break

    case InvoiceSelectedFiltersConstants.invoiceAmountTo:
      if (value === 'null') {
        state.invoiceAmountTo = null
      } else {
        state.invoiceAmountTo = Number(value)
      }
      break

    case InvoiceSelectedFiltersConstants.currency:
      state.currency = value
      break
    case InvoiceSelectedFiltersConstants.clientId:
      state.clientId = value
      break

    default:
      setError(
        state,
        'invalid category in filter, should be defined in constants locally',
      )
  }

  state.pageNumber = 1
}

export interface FilterStateValidation {
  isValid: boolean
  errorMessages: string[]
}

// ALL Filter data for Invoice table go here
export interface InvoiceSelectedFiltersState {
  pageNumber: number
  pageSize: RsmPaginationPageSizes
  searchString: string | undefined
  invoiceStatus: string[]
  invoiceStartDate: Date | null
  invoiceEndDate: Date | null
  invoiceDateRange: string
  invoiceAmountFrom: number | null
  invoiceAmountTo: number | null
  currency: string
  invoiceIds: string[]
  filterStateValidation: FilterStateValidation
  sortby: string | undefined // Add SortBy property
  isSortAscending: boolean | true // Add isSortAscending property
  clientId: string | undefined
  invoiceCounts: {
    filteredInvoices: number
    totalInvoices: number
    totalInvoicesSet: boolean
  }
  exportStatus?: {
    isSuccess: boolean | null
    showAlert: boolean
  }
}

// Define the initial state
const initialState: InvoiceSelectedFiltersState = {
  pageNumber: 1,
  pageSize: 10,
  searchString: '',
  invoiceStatus: [],
  invoiceStartDate: null,
  invoiceEndDate: null,
  invoiceDateRange: '',
  invoiceAmountFrom: null,
  invoiceAmountTo: null,
  currency: InvoiceSelectedFiltersConstants.DefaultCurrency,
  invoiceIds: [],
  sortby: 'InvoiceDate',
  isSortAscending: false,
  filterStateValidation: {
    isValid: true,
    errorMessages: [],
  },
  clientId: undefined,
  invoiceCounts: {
    filteredInvoices: 0,
    totalInvoices: 0,
    totalInvoicesSet: false,
  },
  exportStatus: {
    isSuccess: null,
    showAlert: false,
  },
}

export const invoiceSelectedFiltersSlice = createSlice({
  name: 'invoiceSelectedFilters',
  initialState,
  reducers: {
    clearAllFilters: (): InvoiceSelectedFiltersState => initialState,
    clearCategoryFilters(state, { payload }: PayloadAction<string>) {
      clearCategoryFilter(state, payload)
    },
    setPageSize(state, { payload }: PayloadAction<RsmPaginationPageSizes>) {
      changeRowsPerPage(state, payload)
    },
    toPageNumber(state, { payload }: PayloadAction<number>) {
      changePageNumber(state, payload)
    },
    toNextPage(state) {
      changePageNumber(state, state.pageNumber + 1)
    },
    toPreviousPage(state) {
      changePageNumber(state, state.pageNumber - 1)
    },
    updateFilterValue(state, { payload }: PayloadAction<string>) {
      changeFilterValue(state, payload)
    },
    updateSearch(state, { payload }: PayloadAction<string>) {
      state.searchString = payload
      state.pageNumber = 1
    },
    updateSorting(
      state,
      { payload }: PayloadAction<{ sortBy: string; isAscending: boolean }>,
    ) {
      changeSorting(state, payload.sortBy, payload.isAscending)
    },
    setInvoiceCounts: (
      state,
      action: PayloadAction<{
        filteredInvoices: number
        totalInvoices?: number
      }>,
    ) => {
      state.invoiceCounts.filteredInvoices = action.payload.filteredInvoices
      if (
        action.payload.totalInvoices !== undefined &&
        !state.invoiceCounts.totalInvoicesSet
      ) {
        state.invoiceCounts.totalInvoices = action.payload.totalInvoices
        state.invoiceCounts.totalInvoicesSet = true
      }
    },
    setExportStatus: (
      state,
      action: PayloadAction<{ isSuccess: boolean; showAlert: boolean }>,
    ) => {
      state.exportStatus = {
        isSuccess: action.payload.isSuccess,
        showAlert: action.payload.showAlert,
      }
    },
    clearExportStatus: (state) => {
      state.exportStatus = { isSuccess: null, showAlert: false }
    },
  },
})

export const {
  clearAllFilters,
  clearCategoryFilters,
  setPageSize,
  toPageNumber,
  toNextPage,
  toPreviousPage,
  updateFilterValue,
  updateSearch,
  updateSorting,
  setInvoiceCounts,
  setExportStatus,
  clearExportStatus,
} = invoiceSelectedFiltersSlice.actions

export const getPageInfo = (state: RootState) => ({
  pageNumber: state.invoiceSelectedFilters.pageNumber,
  pageSize: state.invoiceSelectedFilters.pageSize,
})

export const getInvoiceSelectedFilters = (state: RootState) =>
  state.invoiceSelectedFilters

export const getAmountRangeFilterMax = (): number => amountRangeFilterMax
// retrieval of max amount range value from central location to be used in InvoicesChips.tsx and InvoicesAmountRangeFilter.tsx
// setting max to 1000000 due to steps in currency range slider dividing by 100

export const getInvoiceCounts = (state: RootState) =>
  state.invoiceSelectedFilters.invoiceCounts

export const getExportStatus = (state: RootState) =>
  state.invoiceSelectedFilters.exportStatus

export default invoiceSelectedFiltersSlice.reducer
